import React, { FC, useCallback, useReducer, useState } from 'react';
import * as R from 'ramda';
import { action } from 'typesafe-actions';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { beneficiaryService } from '../../../../../channels/secure-web/services';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { Beneficiary } from '../../domain/Beneficiary/entities';
import { BeneficiariesContext, BeneficiariesDispatchContext } from './BeneficiariesContext';
import { BeneficiariesReducer } from './BeneficiariesReducer';
import { defaultBeneficiariesState } from './defaultState';
import { BeneficiariesActionEnum, BeneficiariesContextActionTypes, BeneficiariesListFiltersType } from './types';

const BeneficiariesProvider: FC<{ hasPagination?: boolean }> = ({ children, hasPagination = true }) => {
  // Get initial state values
  const customerId = useSelectedCustomerId();
  const beneficiariesIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.AliasList);
  const [state, dispatch] = useReducer(BeneficiariesReducer, defaultBeneficiariesState);
  const [timestamp] = useState(new Date().toISOString());
  // Set the services and its implementation on provider mount

  // Define api call conditions
  const shouldFetchBeneficiaries = Boolean(customerId) && beneficiariesIsAllowed;
  const shouldDeleteBeneficiary =
    Boolean(state.selectedBeneficiary) &&
    Boolean(customerId) &&
    !isEmpty(state.action) &&
    state.action === BeneficiariesActionEnum.delete &&
    Boolean(state.beneficiaryToDelete) &&
    beneficiariesIsAllowed;

  // Define api calls
  const {
    data: beneficiariesListData,
    mutate: beneficiariesRefetch,
    error: beneficiariesDataError,
    isLoading: isLoadingBeneficiaries,
  } = useInvokeSWRService(
    beneficiaryService.GetBeneficiaries,
    {
      customerId,
      page: hasPagination ? state.filters.page : undefined,
      perPage: hasPagination ? state.filters.perPage : undefined,
      all: !hasPagination,
      meta: {
        timestamp,
      },
    },
    shouldFetchBeneficiaries,
    'beneficiariesService.GetBeneficiaries',
  );

  const {
    data: deleteBeneficiaryData,
    error: deleteBeneficiaryError,
    isLoading: isLoadingDeleteBeneficiary,
  } = useInvokeSWRService(
    beneficiaryService.DeleteBeneficiary,
    state.selectedBeneficiary === null
      ? {}
      : {
          customerId,
          beneficiaryId: state.beneficiaryToDelete?.id,
          meta: {
            timestamp: state.beneficiaryToDelete?.timestamp,
          },
          successCallback: () => beneficiariesRefetch && beneficiariesRefetch(),
        },
    shouldDeleteBeneficiary,
    'beneficiariesService.DeleteBeneficiary',
  );

  const onBeneficiaryAction = useCallback(
    (selectedBeneficiary: Beneficiary, actionName: BeneficiariesActionEnum) => {
      if (!selectedBeneficiary) {
        return;
      }

      if (beneficiariesListData && beneficiariesListData.beneficiaries && beneficiariesListData.beneficiaries.length) {
        const beneficiaryToSelect = R.find(
          beneficiary => beneficiary.id === selectedBeneficiary.id,
          beneficiariesListData.beneficiaries,
        );

        if (!beneficiaryToSelect) {
          return dispatch(action(BeneficiariesContextActionTypes.SetSelectedBeneficiary, null));
        }

        if (actionName === BeneficiariesActionEnum.delete) {
          dispatch(
            action(BeneficiariesContextActionTypes.SetSelectedBeneficiary, {
              selectedBeneficiary: { ...beneficiaryToSelect, timestamp: Date.now().toString() },
              action: actionName,
            }),
          );
        }
      } else {
        dispatch(action(BeneficiariesContextActionTypes.SetSelectedBeneficiary, null));
      }
    },
    [beneficiariesListData],
  );

  const onDeleteBeneficiary = useCallback(() => {
    if (!state.selectedBeneficiary) {
      return;
    }
    dispatch(
      action(BeneficiariesContextActionTypes.DeleteBeneficiary, {
        ...state.selectedBeneficiary,
        timestamp: Date.now().toString(),
      }),
    );
    beneficiariesRefetch && beneficiariesRefetch();
  }, [state.selectedBeneficiary, beneficiariesRefetch]);

  const onResetSelectedBeneficiary = useCallback(() => {
    dispatch(action(BeneficiariesContextActionTypes.ResetSelectedBeneficiary, null));
  }, []);

  const onBeneficiariesListFiltersChange = useCallback(
    (newFilters: BeneficiariesListFiltersType) => {
      dispatch(
        action(BeneficiariesContextActionTypes.SetBeneficiariesListFilters, {
          page: newFilters.page ?? state.filters.page,
          perPage: newFilters?.perPage ?? state.filters.perPage,
        }),
      );
    },
    [state.filters.page, state.filters.perPage],
  );

  return (
    <BeneficiariesContext.Provider
      value={{
        ...state,
        beneficiaries: beneficiariesListData?.beneficiaries ?? [],
        totalBeneficiaries: beneficiariesListData?.totalResults ?? 0,
        onBeneficiaryAction,
        onDeleteBeneficiary,
        onResetSelectedBeneficiary,
        onBeneficiariesListFiltersChange,
        loadingState: {
          isLoadingBeneficiaries,
          isLoadingDeleteBeneficiary,
        },
        errorState: {
          beneficiariesDataError,
          deleteBeneficiaryError,
        },
        deleteBeneficiaryData,
        beneficiariesRefetch,
      }}
    >
      <BeneficiariesDispatchContext.Provider value={dispatch}>{children}</BeneficiariesDispatchContext.Provider>
    </BeneficiariesContext.Provider>
  );
};

export { BeneficiariesProvider };
